import React, { useState, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router";

import { ServerFailed } from "../errors/serverFailed";
import { PageNotFound } from "../errors/pageNotFound";

import { ServerResponse } from "../../services/server/OldWebClient";
import { BuilderAssetService, SaveBuilderAssetRequest } from "../../../src/services/BuilderAssetService";
import { BuilderPreferenceService } from "../../../src/services/BuilderPreferenceService";
import { TelemetryService } from "../../../src/services/TelemetryService";
import { ShareService } from "../../services/ShareService";
import { AssetWritingLogService } from "../../services/AssetWritingLogService";

import { BuilderActionLocations, EventTracking } from "../../../src/utilities/Tracking/EventTracking";

import { BuilderAsset } from "../../../src/entities/BuilderAsset";
import { ApplicationUser } from "../../../src/entities/ApplicationUser";
import AppConfig from "../../../src/entities/AppConfig";
import { BuilderPreferences } from "../../../src/entities/BuilderPreferences";
import {AssetWritingLogs} from "../../entities/AssetWritingLogs";

import { ContentDrawer } from "./drawer/contentDrawer";
import BuilderSnackbar from "./editor/BuilderSnackbar";
import EditorContext from "./editor/EditorContext";
import { ShareDialog } from "../../components/ShareDialog/ShareDialog";
import { SharingDialog } from "../../components/SharingDialog/SharingDialog";
import { SaveAsTemplateDialog } from "../../components/SaveAsTemplateDialog";

import { BuilderAppBarAction, BuilderContext, SelectedContent } from "./useBuilder";
import { Builder } from "./views/Builder";
import { UserAgent } from "../../utilities/UserAgent";
import { useOfflineAssets } from "../offline/useOfflineAssets";
import { ExportSlidesModal } from './drawer/components/exportSlidesModal';
import { FieldValidationError } from "../../services/server/ServerResponses";
import { AddAssetAction } from "../../entities/AddAssetAction";
import { CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@material-ui/core";

interface Props extends RouteComponentProps<{ id: string }> {
	user: ApplicationUser | null;
	config: AppConfig;
	sermonAsset?: BuilderAsset;
}

const defaultPreferences: BuilderPreferences = {
	bibleTranslationId: "NIV",
	bibleSearchCopySetting: "all",
	bibleSearchViewSetting: "all",
	builderExploreDrawerOpen: true,
	builderSlidesDrawerOpen: true,
	hasUploadedSlideImage: false,
	slideExporterSelectedGalleryImageId: 1,
	bibleSearchShowVerseNumbers: false,
	hideConsecutiveSentences: false,
	hideFleschReadingEase: false,
	hideParagraphLength: false,
	hidePassiveVoice: false,
	hideScriptureSaturation: false,
	hideSentenceLength: false,
	hideSubheadingDistribution: false,
	hideTransitionalWords: false,
	textToSpeechSpeed: 1,
	textToSpeechVoice: "en-US-BenjaminRUS",
};

export function BuilderHome(props: Props) {
	const { onlineMode, offlineBuilderAssets } = useOfflineAssets();
	const { user, config, match, history, sermonAsset } = props;

	const [selectedAsset, setSelectedAsset] = useState<BuilderAsset | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [serverFailed, setServerFailed] = useState<boolean>(false);
	const [pageNotFound, setPageNotFound] = useState<boolean>(false);
	const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
	const [showExportSlides, setShowExportSlides] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [selectedSermonId, setSelectedSermonId] = useState<string | undefined>(undefined);
	const [selectedIllustrationId, setSelectedIllustrationId] = useState<string | undefined>(undefined);
	const [selectedScriptureReference, setSelectedScriptureReference] = useState<string | undefined>(undefined);
	const [preferences, setPreferences] = useState<BuilderPreferences>(defaultPreferences);
	const [contentDrawerOpen, setContentDrawerOpen] = useState<boolean>(false);
	const [openShareDialog, setOpenShareDialog] = useState<boolean>(false);
	const [openSaveAsTemplateDialog, setOpenSaveAsTemplateDialog] = useState<boolean>(false);
	const activeMakerSubscription = useMemo(() => user !== null && user.makerSubscription !== undefined, [user]);
	const [openUnshareNonPaidDialog, setOpenUnshareNonPaidDialog] = useState<boolean>(false);
	const [shareDialogFromSlides, setShareDialogFromSlides] = useState<boolean>(false);
	const [openNewSharingDialog, setOpenNewSharingDialog] = useState<boolean>(false);
	const [fieldErrors, setFieldErrors] = useState<FieldValidationError[]>([]);
	const [writingLogs, setWritingLogs] = useState<AssetWritingLogs>({ writingLogs: [], id: "", totalTime: 0 });
	const [customImageAddedClient64Base, setCustomImageAddedClient64Base] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (onlineMode) {
			mountComponent();
		} else {
			mountOfflineComponent();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const mountComponent = async () => {
		document.title = "Sermon Maker - Builder";
		const builderAssetId = match.params.id;

		if(sermonAsset){
			setSelectedAsset(sermonAsset);
			setLoading(false);
			return;
		}

		try {
			const builderAssetResponse = await BuilderAssetService.accessAsset(builderAssetId);
			if (ServerResponse.isError(builderAssetResponse)) {
				if (builderAssetResponse.statusCode === 404) {
					setPageNotFound(true);
					setLoading(false);
				}
				if (builderAssetResponse.statusCode === 500) {
					setServerFailed(true);
					setLoading(false);
				}
				return;
			} else if (ServerResponse.isModelValidation(builderAssetResponse)) {
				setServerFailed(true);
				setLoading(false);
				return;
			}

			let builderAsset = builderAssetResponse.data;

			setSelectedAsset(builderAsset);
			setLoading(false);

			document.title = `${builderAsset.name}`;
		} catch {
			setPageNotFound(true);
			setLoading(false);
		}

		try {
			let preferencesRequest = await BuilderPreferenceService.getPreferences();
			const writingLogs = await AssetWritingLogService.getAssetWritingLogs(builderAssetId);

			if (ServerResponse.isSuccess(preferencesRequest)) {
				let preferences = preferencesRequest.data;

				if (preferences.slideExporterSelectedGalleryImageId == null) {
					preferences.slideExporterSelectedGalleryImageId = 1;
				}

				setPreferences(preferences);
			} else if (ServerResponse.isError(preferencesRequest)) {
				setBuilderAlert("Loading your preferences failed.");
			}

			if (ServerResponse.isSuccess(writingLogs)) {
				let logs = writingLogs.data;

				if (logs.totalTime !== 0) {
					setWritingLogs(logs);
				}
			} else if (ServerResponse.isError(writingLogs)) {
				setBuilderAlert("Writing Logs not available");
			}
		} catch {
			// Use default preferences
		}

		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_builder", now.toString());
	};

	const mountOfflineComponent = () => {
		document.title = "Sermon Maker - Builder";
		const builderAssetId = match.params.id;

		const foundLocalAsset = offlineBuilderAssets.find((a) => a.id === builderAssetId);
		setLoading(false);

		if (foundLocalAsset === undefined) {
			setPageNotFound(true);
			return;
		}

		setSelectedAsset(foundLocalAsset);
		document.title = `${foundLocalAsset.name}`;
	};

	const copyToClipboard = (text: string, nameOfThingCopied: string) => {
		if (!navigator.clipboard) {
			setBuilderAlert("Error: It appears your web browser does not support copying.");
			return;
		}
		try {
			navigator.clipboard.writeText(text);
			notifyClipboardCopy(nameOfThingCopied);
		} catch (err) {
			setBuilderAlert("Error: " + err);
			return;
		}
	};

	const notifyClipboardCopy = (nameOfThingCopied: string) => {
		let pasteButton = document.getElementById("pasteButton");
		if (pasteButton) {
			pasteButton.classList.add("pasteActive");
		}
		setBuilderAlert(`${nameOfThingCopied} copied to clipboard`);
	};

	const closeContentDrawer = () => {
		setSelectedSermonId(undefined);
		setSelectedIllustrationId(undefined);
		setSelectedScriptureReference(undefined);
	};

	const onSavePreferences = (updatedPreferences: BuilderPreferences) => {
		if (updatedPreferences.slideExporterSelectedGalleryImageId !== preferences.slideExporterSelectedGalleryImageId) {
			EventTracking.Builder.onPreferencesChanges("Slides", `Background ${updatedPreferences.slideExporterSelectedGalleryImageId}`);
		}

		if (updatedPreferences.bibleTranslationId !== preferences.bibleTranslationId) {
			EventTracking.Builder.onPreferencesChanges("Slides", `Bible Translation - ${updatedPreferences.bibleTranslationId}`);
		}
		setPreferences(updatedPreferences);
		BuilderPreferenceService.savePreferences(updatedPreferences);
	};

	const onRefreshWritingLog = async () => {
		const asset = selectedAsset;
		if (asset) {
			let result = await AssetWritingLogService.getAssetWritingLogs(asset.id);
			if (ServerResponse.isSuccess(result)) {
				let logs = result.data;

				if (logs.totalTime !== 0) {
					setWritingLogs(logs);
				}
			}
		}
	};

	const onPresent = (location:BuilderActionLocations) => {
		const asset = selectedAsset;
		if (asset) {
			EventTracking.Builder.onPresentSermon(location)
			history.push(`/present/${asset.id}`);
		}
	};

	const onGoBack = () => {
		if (props.location.state) {
			history.goBack();
		} else {
			history.push("/");
		}
	};

	// const toggleShareBuilderAsset = async (isShared: boolean) => {
	// 	if (!selectedAsset) {
	// 		return;
	// 	}

	// 	if (selectedAsset.shareId && !activeMakerSubscription && !isShared) {
	// 		setOpenUnshareNonPaidDialog(true);
	// 		return;
	// 	}
	// 	const item = selectedAsset;

	// 	if (activeMakerSubscription) {
	// 		if (!item.shareId || isShared) {
	// 			await share(item);
	// 			setOpenShareDialog(true);
	// 		} else {
	// 			await unshare(item);
	// 		}
	// 	} else {
	// 		window.location.assign(`https://www.sermonmaker.net/upgrade-now/?upgradePath=share&upgradeAction=${AddAssetAction.ShareAsset}`);
	// 	}
	// };

	// const share = async (item: BuilderAsset) => {
	// 	let shareResponse = await ShareService.share(item.id);

	// 	if (ServerResponse.isSuccess(shareResponse)) {
	// 		let oldItem = selectedAsset;

	// 		if (oldItem) {
	// 			oldItem.shareId = shareResponse.data.shareId;
	// 			oldItem.isShared = shareResponse.data.isShared;
	// 		}
	// 	} else if (ServerResponse.isError(shareResponse)) {
	// 		setBuilderAlert("Toggling share was unsuccessful. Please try again.");
	// 	} else {
	// 		setBuilderAlert("An unknown error occurred. Please try again.");
	// 	}
	// };

	const unshare = async (item: BuilderAsset) => {
		let shareResponse = await ShareService.unshare(item.id);

		if (ServerResponse.isSuccess(shareResponse)) {
			let oldItem = selectedAsset;

			if (oldItem) {
				oldItem.shareId = shareResponse.data.shareId;
				oldItem.isShared = shareResponse.data.isShared;
			}
		} else if (ServerResponse.isError(shareResponse)) {
			setBuilderAlert("Toggling share was unsuccessful. Please try again.");
		} else {
			setBuilderAlert("An unknown error occurred. Please try again.");
		}
	};

	const handleShare = (isSlides: boolean, location:BuilderActionLocations) => {
		EventTracking.Builder.onShare(location);
		if (activeMakerSubscription) {
			if (!isSlides && selectedAsset) {
				setOpenNewSharingDialog(true);
			} else {
				setOpenShareDialog(true);
				setShareDialogFromSlides(isSlides);
			}
		} else {
			window.location.assign(`https://www.sermonmaker.net/upgrade-now/?upgradePath=share&upgradeAction=${AddAssetAction.ShareAsset}`);
		}
	};

	const handleCustomTemplate = (location:BuilderActionLocations) => {
		EventTracking.Builder.onCustomTemplate(location);
		if (activeMakerSubscription) {
			setOpenSaveAsTemplateDialog(!openSaveAsTemplateDialog);
		} else {
			window.location.assign(
				`https://www.sermonmaker.net/upgrade-now/?upgradePath=CustomTemplate&upgradeAction=${AddAssetAction.CreateCustomTemplate}`
			);
		}
	};

	const updateAsset = (isShared: boolean, shareId: string) => {
		setSelectedAsset({
			...selectedAsset!,
			shareId: shareId,
			isShared: isShared,
		});
	};

	const onConfirmUnshareNonPaid = async (e: React.FormEvent<HTMLElement>) => {
		e.preventDefault();

		if (!selectedAsset) {
			return;
		}

		setOpenUnshareNonPaidDialog(false);
		await unshare(selectedAsset);
	};

	const onSaveAsTemplateConfirm = async (title: string, desciption?: string) => {
		if (selectedAsset) {
			const asset = await BuilderAssetService.get(selectedAsset!.id);

			if (ServerResponse.isSuccess<BuilderAsset>(asset)) {
				let request: SaveBuilderAssetRequest = {
					parentId: asset.data.parentId,
					name: title,
					isStarred: asset.data.isStarred,
					dateAccessed: new Date(),
					category: asset.data.category,
					text: asset.data.text,
					slideCount: asset.data.slideCount,
					isTemplate: true,
					templateDescription: desciption ? desciption : "",
					slideExporterAspectRatio: asset.data.slideExporterAspectRatio,
					customSlideBackgroundImagePath: asset.data.customSlideBackgroundImagePath,
					offlineMode: false,
					lastModifiedFormFactor: UserAgent.getFormFactor(),
					lastModifiedOnOperatingSystem: UserAgent.getOperatingSystem(),
				};
				let response = await BuilderAssetService.saveTemplate(request);
				if (ServerResponse.isSuccess<BuilderAsset>(response)) {
					setOpenSaveAsTemplateDialog(false);
					setBuilderAlert("Template created and can be found in your library");
				} else if (ServerResponse.isModelValidation(response)) {
					setFieldErrors(response.errors);
				} else if (ServerResponse.isError(response)) {
					setOpenSaveAsTemplateDialog(false);
					setBuilderAlert("Error: Server error, please try again later.");
				}
			}
		}
	};

	const handleAppBarAction = (action: BuilderAppBarAction, location:BuilderActionLocations) => {
		switch (action) {
			case BuilderAppBarAction.GoBack:
				return onGoBack();
			case BuilderAppBarAction.Present:
				return onPresent(location);
			case BuilderAppBarAction.SaveAsTemplate:
				return handleCustomTemplate(location);
			case BuilderAppBarAction.Share:
				return handleShare(false, location);
		}
	};

	const handleContentDetailSelection = (selectedContent: SelectedContent) => {
		if (selectedContent.type === "Bible Verse") {
			setContentDrawerOpen(!contentDrawerOpen);
			setSelectedScriptureReference(selectedContent.selectedScriptureReference);
		} else if (selectedContent.type === "Illustration") {
			setContentDrawerOpen(!contentDrawerOpen);
			setSelectedIllustrationId(selectedContent.selectedIllustrationId);
		} else {
			setContentDrawerOpen(!contentDrawerOpen);
			setSelectedSermonId(selectedContent.selectedSermonId);
		}
	};

	const setBuilderAlert = (message: string) => {
		setSnackbarMessage(message);
		setSnackbarOpen(true);
	};

	const onSlideExport = (location:BuilderActionLocations) => {
		setShowExportSlides(!showExportSlides);
		EventTracking.Builder.onPresentSlides(location);
	}

	if (pageNotFound) {
		return <PageNotFound errorMessage="We're sorry, but we couldn't find the Sermon Asset you are trying to edit." />;
	}
	if (serverFailed) {
		return <ServerFailed errorMessage="We're sorry, but we're having trouble loading your Sermon Asset in the builder." />;
	}

	const builderAsset = selectedAsset;

	if (loading || !builderAsset) {
		return (
			<div style={{ textAlign: "center" }}>
				<CircularProgress style={{ marginTop: "50px" }} />
				<br />
				<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
					Loading...
				</Typography>
			</div>
		);
	}

	return (
		<BuilderContext.Provider
			value={{
				builderAsset,
				setBuilderAsset: (a) => setSelectedAsset(a),
				preferences,
				savePreferences: onSavePreferences,
				customImageBase64Content: customImageAddedClient64Base,
				setCustomImageBase64Content: (content) => setCustomImageAddedClient64Base(content),
				handleAppBarAction: handleAppBarAction,
				handleContentDetailSelection: handleContentDetailSelection,
				copyToClipboard: (content, type) => copyToClipboard(content, type),
				showShareDialog: handleShare,
				setAlert: setBuilderAlert,
				appConfig: config,
				user: user,
				writingLogs: writingLogs,
				refreshWritingLog: onRefreshWritingLog,
				toggleSlideExportDialog: onSlideExport,
			}}
		>
			<EditorContext>
				<Builder onCloseContentDrawer={closeContentDrawer} />
				{(selectedSermonId || selectedIllustrationId || selectedScriptureReference) && (
					<ContentDrawer
						sermonId={selectedSermonId}
						illustrationId={selectedIllustrationId}
						scriptureReference={selectedScriptureReference}
						preferences={preferences}
						onDrawerClose={closeContentDrawer}
						onClipboardCopy={notifyClipboardCopy}
						setBuilderAlert={setBuilderAlert}
					/>
				)}
				{selectedAsset && openShareDialog && user && (
					<ShareDialog
						onClose={() => setOpenShareDialog(false)}
						isOpen={openShareDialog}
						onShareChangeSuccess={(id, isShared, shareId) => updateAsset(isShared, shareId)}
						onError={setBuilderAlert}
						id={selectedAsset.id}
						shareFromSlides={shareDialogFromSlides}
						onGiveMessage={setBuilderAlert}
					/>
				)}
				{selectedAsset && openNewSharingDialog && user && (
					<SharingDialog
						onClose={() => setOpenNewSharingDialog(false)}
						isOpen={openNewSharingDialog}
						onError={setBuilderAlert}
						id={selectedAsset.id}
						onGiveMessage={setBuilderAlert}
					/>
				)}
				{selectedAsset && openSaveAsTemplateDialog && user && (
					<SaveAsTemplateDialog
						onClose={() => setOpenSaveAsTemplateDialog(false)}
						isOpen={openSaveAsTemplateDialog}
						onSave={onSaveAsTemplateConfirm}
						errors={fieldErrors}
					/>
				)}
				{selectedAsset && (
					<Dialog
						open={openUnshareNonPaidDialog}
						onClose={() => setOpenUnshareNonPaidDialog(false)}
						aria-labelledby="unshare-form-dialog-title"
					>
						<form onSubmit={(e: React.FormEvent<HTMLElement>) => onConfirmUnshareNonPaid(e)}>
							<DialogTitle id="rename-form-dialog-title">Confirm Unshare</DialogTitle>
							<DialogContent>
								<DialogContentText>
									As a free user, once you stop sharing this resource, you will not be able to share it again unless you upgrade
									your account. Still want to stop sharing?
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								<Button onClick={() => setOpenUnshareNonPaidDialog(false)} color="primary">
									Cancel
								</Button>
								<Button type="submit" color="primary">
									Unshare
								</Button>
							</DialogActions>
						</form>
					</Dialog>
				)}
				{ showExportSlides &&
					<ExportSlidesModal
					config={config.googleSlidesApi}
					open={showExportSlides}
					handleClose={() => setShowExportSlides(!showExportSlides)}
					assetId={builderAsset.id}
					hasCustomImage={builderAsset.customSlideBackgroundImagePath != null}
					defaultImageId={preferences.slideExporterSelectedGalleryImageId!}
					imageAspectRatio={builderAsset.slideExporterAspectRatio ?? "16x9"}
					customSlideBackgroundImagePath={builderAsset.customSlideBackgroundImagePath}
					/>
				}
				<BuilderSnackbar snackbarOpen={snackbarOpen} snackbarMessage={snackbarMessage} onClose={() => setSnackbarOpen(false)} />
			</EditorContext>
		</BuilderContext.Provider>
	);
}